import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function AdminMatches() {
  const [matches, setMatches] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Función para cargar los partidos desde el backend
  const loadMatches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Si no hay token, redirigir al login inmediatamente
      if (!token) {
        navigate('/login');
        return;
      }

      const res = await api.get('/matches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMatches(res.data);
      
      // Inicializar el estado de los inputs con los resultados actuales
      const initialScores = {};
      res.data.forEach(m => {
        initialScores[m.id] = { 
          home: m.scoreHome !== null ? m.scoreHome : '', 
          away: m.scoreAway !== null ? m.scoreAway : '' 
        };
      });
      setScores(initialScores);
    } catch (err) {
      console.error("Error detallado:", err.response?.data || err.message);
      alert("Error: " + (err.response?.data?.message || "No se pudieron cargar los partidos"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, []);

  // Función para guardar el resultado (Llama a tu endpoint @Patch :id/result)
  const handleUpdateResult = async (matchId) => {
    const s = scores[matchId];
    
    if (s.home === '' || s.away === '') {
      return alert("Ingresa ambos marcadores.");
    }

    try {
      const token = localStorage.getItem('token');
      await api.patch(`/matches/${matchId}/result`, {
        scoreHome: parseInt(s.home),
        scoreAway: parseInt(s.away)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert("✅ Resultado confirmado. Los puntos se han recalculado.");
      loadMatches(); // Recargar para ver los cambios reflejados
    } catch (err) {
      console.error("Error al actualizar:", err.response?.data || err.message);
      alert("No se pudo guardar: " + (err.response?.data?.message || "Error del servidor"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 font-black uppercase italic text-slate-400">
        Cargando Panel Administrativo...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-white text-slate-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase shadow-sm hover:bg-slate-50 border border-slate-200"
          >
            ← Volver al Dashboard
          </button>
          <h1 className="text-xl font-black text-slate-900 uppercase italic">🛠️ Administrar Resultados</h1>
        </div>

        {/* LISTA DE PARTIDOS */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          <div className="bg-slate-900 p-4 text-white text-[10px] font-bold uppercase tracking-widest text-center">
            Resultados Reales de la Competición
          </div>

          <div className="divide-y divide-slate-100">
            {matches.map((m) => {
              const hasResult = m.scoreHome !== null && m.scoreAway !== null;
              
              return (
                <div key={m.id} className={`p-4 flex flex-col md:flex-row items-center gap-6 transition-all ${hasResult ? 'bg-green-50/40' : 'bg-white'}`}>
                  
                  {/* Fecha y Grupo */}
                  <div className="w-24 text-center md:text-left">
                    <p className="text-[9px] font-black text-blue-600 uppercase">
                      {new Date(m.matchDate).toLocaleDateString()}
                    </p>
                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                      ID: {m.id} | Grupo {m.homeTeam?.group}
                    </p>
                  </div>

                  {/* Equipos e Inputs */}
                  <div className="flex-1 flex items-center justify-center gap-4">
                    {/* Local */}
                    <div className="flex flex-col items-center flex-1">
                      <img src={m.homeTeam?.flagUrl} className="w-8 h-5 shadow-sm border mb-1 object-cover" alt="" />
                      <span className="text-[10px] font-black uppercase text-slate-700 text-center">{m.homeTeam?.name}</span>
                    </div>

                    {/* Score inputs */}
                    <div className="flex items-center gap-2">
                      <input 
                        type="number"
                        value={scores[m.id]?.home}
                        onChange={(e) => setScores({
                          ...scores,
                          [m.id]: { ...scores[m.id], home: e.target.value }
                        })}
                        className="w-12 h-12 border-2 border-slate-200 rounded-xl text-center font-black text-lg focus:border-blue-500 focus:bg-blue-50 outline-none transition-all"
                      />
                      <span className="text-slate-300 font-black">-</span>
                      <input 
                        type="number"
                        value={scores[m.id]?.away}
                        onChange={(e) => setScores({
                          ...scores,
                          [m.id]: { ...scores[m.id], away: e.target.value }
                        })}
                        className="w-12 h-12 border-2 border-slate-200 rounded-xl text-center font-black text-lg focus:border-blue-500 focus:bg-blue-50 outline-none transition-all"
                      />
                    </div>

                    {/* Visitante */}
                    <div className="flex flex-col items-center flex-1">
                      <img src={m.awayTeam?.flagUrl} className="w-8 h-5 shadow-sm border mb-1 object-cover" alt="" />
                      <span className="text-[10px] font-black uppercase text-slate-700 text-center">{m.awayTeam?.name}</span>
                    </div>
                  </div>

                  {/* Botón de Acción */}
                  <div className="w-full md:w-32">
                    <button
                      onClick={() => handleUpdateResult(m.id)}
                      className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md
                        ${hasResult 
                          ? 'bg-slate-800 text-white hover:bg-black' 
                          : 'bg-green-600 text-white hover:bg-green-700 shadow-green-100 active:scale-95'}`}
                    >
                      {hasResult ? 'Corregir' : 'Finalizar'}
                    </button>
                  </div>

                </div>
              );
            })}
          </div>
          
          {matches.length === 0 && (
            <div className="p-20 text-center text-slate-400 font-bold uppercase text-xs tracking-widest italic">
              No se encontraron partidos en la base de datos.
            </div>
          )}
        </div>
        
        <p className="mt-6 text-center text-[9px] text-slate-400 uppercase font-bold tracking-widest">
          ⚠️ Al finalizar un partido, los puntos de todos los usuarios se actualizarán automáticamente.
        </p>
      </div>
    </div>
  );
}